import { _HTMLElement } from '../utils';
import { withAttributeReflection } from '../mixins/attribute-reflection';
import { withLifecycle } from '../mixins/lifecycle';
import { withShadowDOM } from '../mixins/shadow-dom';
import { withTemplate } from '../mixins/template';
import { withUpdate } from '../mixins/update';

/**
 * The `withHTMLComponent` compound mixin combines a set mixins necessary to create a web component with ShadowDOM. It's intended for simple components that don't require complex rendering. It renders the given `html` immediately and relies on manual DOM manipulation for dynamic content.
 *
 * Mixins used:
 * - `withAttributeReflection`
 * - `withLifecycle`
 * - `withShadowDOM`
 * - `withTemplate`
 * - `withUpdate`
 *
 * @category mixin - compound
 * @param {String} styles CSS string that's shimmed for ShadowDOM use and automatically attached to the ShadowDOM for each instance.
 * @param {String} html HTML string that's cloned into a DOM node and automatically attached to the ShadowDOM for each instance.
 * @param {Class} [Base=HTMLElement] Optional base class to extend from.
 * @returns {Class}
 *
 * @example
```js
import { withHTMLComponent } from '@updater-legacy/components/custom-element';
const html = `<div id="container"><input /></div>`;
import styles from './index.css';

class Input extends withHTMLComponent(styles, html)() {
  static props = ['value'];
  static reflectedAttributes = [
    { 
      name: 'disabled',
      boolean: true,
    },
    {
      name: 'type',
    }
  ];

  updated({ value }) {
    this.shadowRoot.querySelector('#input').value = value;
  }
}
 ```
 */
export const withHTMLComponent = (styles, html) => (Base = _HTMLElement) =>
  class extends withShadowDOM(styles)(
    withTemplate(html)(withUpdate(withAttributeReflection(withLifecycle(Base))))
  ) {
    constructor() {
      super();

      this.shadowRoot.appendChild(this._styles);
      this.shadowRoot.appendChild(this._template);
    }
  };
